import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { operatorKeys } from '~/lib/query-keys';
import type { OperatorRunWire } from '~/hooks/queries/use-operator-runs';

/**
 * FRONT-1989: the desktop-task card's Stop button.
 * `POST /api/operator/runs/:runId/stop` is owner-scoped and idempotent on a
 * finished run (per the "Agreed seam with step 6" contract: it 200s with the
 * run unchanged rather than 404ing), so unlike
 * `useCancelBackgroundChat`/`shouldToastCancelError` there is no race case to
 * special-case here — any error really is one. The response is the full run
 * object, written straight into `operatorKeys.run(id)` (same
 * write-the-response-into-cache pattern as `useSaveCalibration`) so the card
 * reflects `stopRequested`/`stopped` immediately rather than waiting on the
 * `operator-run-updated` SSE invalidation or the next 3s poll tick.
 */
export function useStopOperatorRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId }: { runId: string }) =>
      api.post<OperatorRunWire>(`/api/operator/runs/${encodeURIComponent(runId)}/stop`, {}),
    // FRONT-1996 review M8: cancel any in-flight GET for this exact run
    // before the request goes out — otherwise a poll that happens to
    // resolve AFTER this mutation's own response writes the fresh state
    // could silently overwrite it with a stale pre-stop snapshot. Same fix
    // as `useTakeOverOperatorRun`/`useHandBackOperatorRun`.
    onMutate: async ({ runId }) => {
      await qc.cancelQueries({ queryKey: operatorKeys.run(runId) });
    },
    onSuccess: (run) => {
      qc.setQueryData(operatorKeys.run(run.id), run);
    },
    onError: (err) => {
      toast.error(`Stop failed: ${(err as Error).message}`);
    },
  });
}
